import {
  addDays,
  eachDayOfInterval,
  endOfWeek,
  format,
  Interval,
  startOfWeek,
} from 'date-fns';
import { useEffect, useState } from 'react';
import { monthNames } from '../commom/monthsNames';
import { useUrlSearchParams } from './useCustomSearchParams';

function useWeekCalendar(
  onSelectDate?: (date: Date, interval: Interval) => void
) {
  const { getUrlParam, setUrlParam } = useUrlSearchParams();
  const initialDate = getInitialDate();
  const [selectedDate, setSelectedDate] = useState(initialDate);
  const [interval, setInteval] = useState(() => {
    return {
      start: startOfWeek(initialDate, { weekStartsOn: 1 }),
      end: endOfWeek(initialDate, { weekStartsOn: 1 }),
    };
  });

  const [weekDatesInterval, setWeekDatesInterval] = useState(() =>
    eachDayOfInterval(interval)
  );

  function handleNextWeek() {
    const nextWeekStart = startOfWeek(addDays(interval.end, 1), {
      weekStartsOn: 1,
    });

    const newInterval = {
      start: startOfWeek(nextWeekStart, { weekStartsOn: 1 }),
      end: endOfWeek(nextWeekStart, { weekStartsOn: 1 }),
    };
    setInteval(newInterval);

    setWeekDatesInterval(eachDayOfInterval(newInterval));
  }

  function handlePrevWeek() {
    const prevWeekStart = startOfWeek(addDays(interval.start, -1), {
      weekStartsOn: 1,
    });

    const newInterval = {
      start: startOfWeek(prevWeekStart, { weekStartsOn: 1 }),
      end: endOfWeek(prevWeekStart, { weekStartsOn: 1 }),
    };
    setInteval(newInterval);

    setWeekDatesInterval(eachDayOfInterval(newInterval));
  }

  function handleSelectDate(date: Date) {
    setSelectedDate(date);

    setUrlParam('date', format(date, 'yyyy-MM-dd'));

    if (onSelectDate) {
      onSelectDate(date, interval);
    }
  }

  function getMonthYearLabel() {
    const { months, years } = weekDatesInterval.reduce(
      (acc, date) => {
        const month = monthNames[date.getMonth()];
        const year = date.getFullYear();

        if (!acc.months.includes(month)) {
          acc.months.push(month);
        }

        if (!acc.years.includes(year)) {
          acc.years.push(year);
        }

        return acc;
      },
      {
        months: [] as string[],
        years: [] as number[],
      }
    );

    if (years.length > 1) {
      return `${months[0]} ${years[0]} / ${months[1]} ${years[1]}`;
    }

    if (months.length > 1) {
      return `${months[0]} / ${months[1]} ${years[0]}`;
    }

    return `${months[0]} ${years[0]}`;
  }

  function getInitialDate() {
    const date = getUrlParam('date');
    if (date) {
      return new Date(date + 'T00:00:00');
    }

    const today = new Date();
    setUrlParam('date', format(today, 'yyyy-MM-dd'));
    return today;
  }

  useEffect(() => {
    const dateParam = getUrlParam('date');
    if (dateParam) {
      handleSelectDate(new Date(dateParam + 'T00:00:00'));
    }
  }, []);

  return {
    monthYearLabel: getMonthYearLabel(),
    weekDatesInterval,
    selectedDate,
    handleNextWeek,
    handlePrevWeek,
    handleSelectDate,
  };
}

export { useWeekCalendar };
